import type { GameSnapshot, SnapshotEntity } from "../render/types";

type SnapshotSample = {
  time: number;
  snapshot: GameSnapshot;
};

const INTERPOLATION_DELAY_MS = 50;
const MAX_INTERPOLATION_SAMPLES = 24;
const MAX_EXTRAPOLATION_MS = 80;
const MAX_INTERPOLATED_DISTANCE = 420;

export class SnapshotInterpolator {
  private readonly samples: SnapshotSample[] = [];

  public push(snapshot: GameSnapshot, time = performance.now()): void {
    const last = this.samples[this.samples.length - 1];
    const tick = snapshotTick(snapshot);
    const lastTick = last ? snapshotTick(last.snapshot) : null;

    if (tick !== null && lastTick !== null && tick <= lastTick) {
      return;
    }

    const safeTime = last ? Math.max(time, last.time + 1) : time;

    this.samples.push({
      time: safeTime,
      snapshot
    });

    while (this.samples.length > MAX_INTERPOLATION_SAMPLES) {
      this.samples.shift();
    }
  }

  public clear(): void {
    this.samples.length = 0;
  }

  public sample(time = performance.now()): GameSnapshot | null {
    if (this.samples.length === 0) {
      return null;
    }

    if (this.samples.length === 1) {
      return this.samples[0]?.snapshot ?? null;
    }

    const targetTime = time - INTERPOLATION_DELAY_MS;

    while (this.samples.length >= 3 && this.samples[1] && this.samples[1].time <= targetTime) {
      this.samples.shift();
    }

    const previous = this.samples[0];
    const next = this.samples[1];

    if (!previous) {
      return null;
    }

    if (!next) {
      return previous.snapshot;
    }

    if (targetTime <= previous.time) {
      return previous.snapshot;
    }

    const duration = Math.max(1, next.time - previous.time);
    const amount =
      targetTime <= next.time
        ? (targetTime - previous.time) / duration
        : 1 + Math.min(targetTime - next.time, MAX_EXTRAPOLATION_MS) / duration;

    return interpolateSnapshot(previous.snapshot, next.snapshot, amount);
  }
}

function interpolateSnapshot(
  previous: GameSnapshot,
  next: GameSnapshot,
  amount: number
): GameSnapshot {
  const previousEntities = new Map<string, SnapshotEntity>();

  for (const entity of previous.entities) {
    previousEntities.set(entityKey(entity), entity);
  }

  return {
    ...next,
    entities: next.entities.map((entity) => {
      const previousEntity = previousEntities.get(entityKey(entity));

      if (!previousEntity) {
        return entity;
      }

      const distance = distanceBetween(previousEntity.position, entity.position);

      if (!Number.isFinite(distance) || distance > MAX_INTERPOLATED_DISTANCE) {
        return entity;
      }

      return {
        ...entity,
        position: {
          x: lerp(previousEntity.position.x, entity.position.x, amount),
          y: lerp(previousEntity.position.y, entity.position.y, amount)
        },
        radius: lerp(previousEntity.radius, entity.radius, clamp01(amount))
      };
    })
  };
}

function entityKey(entity: SnapshotEntity): string {
  return `${String(entity.kind)}:${String(extractId(entity.id) ?? fallbackEntityId(entity))}`;
}

function extractId(value: unknown): number | string | null {
  if (typeof value === "number" || typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object" && "0" in value) {
    const nested = (value as { 0?: unknown })[0];

    if (typeof nested === "number" || typeof nested === "string") {
      return nested;
    }
  }

  return null;
}

function snapshotTick(snapshot: GameSnapshot): number | null {
  return typeof snapshot.tick === "number" && Number.isFinite(snapshot.tick) ? snapshot.tick : null;
}

function fallbackEntityId(entity: SnapshotEntity): string {
  return `${entity.position.x}:${entity.position.y}:${entity.radius}`;
}

function distanceBetween(
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function lerp(a: number, b: number, amount: number): number {
  return a + (b - a) * amount;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}